// Admin API 客户端（鏡像 web 的 client）
//
// 本地开发：VITE_API_BASE_URL 留空，fetch 走相对路径，由 vite proxy 转发 /v1 → server
// 生产部署：VITE_API_BASE_URL = https://api.vivilo.top，fetch 直接打到 Railway

import type { ErrorResponse } from '@vivilo/shared'

const ACCESS_TOKEN_KEY = 'vv_admin_access'
const REFRESH_TOKEN_KEY = 'vv_admin_refresh'

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')
const url = (path: string) => `${API_BASE}${path}`

export class ApiClientError extends Error {
  public readonly code: string
  public readonly statusCode: number
  public readonly details?: Record<string, unknown>

  constructor(code: string, message: string, statusCode: number, details?: Record<string, unknown>) {
    super(message)
    this.code = code
    this.statusCode = statusCode
    this.details = details
  }
}

export const tokenStore = {
  get: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  set: (a: string, r: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, a)
    localStorage.setItem(REFRESH_TOKEN_KEY, r)
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  skipAuthRetry?: boolean
}

let refreshing: Promise<void> | null = null

async function tryRefresh(): Promise<void> {
  const refresh_token = tokenStore.getRefresh()
  if (!refresh_token) throw new ApiClientError('UNAUTHORIZED', '未登录', 401)
  refreshing ??= (async () => {
    try {
      const res = await fetch(url('/v1/auth/refresh'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      })
      if (!res.ok) throw new Error('refresh failed')
      const j = (await res.json()) as { access_token: string; refresh_token: string }
      tokenStore.set(j.access_token, j.refresh_token)
    } finally {
      refreshing = null
    }
  })()
  await refreshing
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' }
  if (opts.body) headers['content-type'] = 'application/json'
  const token = tokenStore.get()
  if (token) headers.authorization = `Bearer ${token}`
  const res = await fetch(url(path), {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'include',
  })
  if (res.status === 401 && !opts.skipAuthRetry) {
    try {
      await tryRefresh()
      return api<T>(path, { ...opts, skipAuthRetry: true })
    } catch {
      tokenStore.clear()
      window.dispatchEvent(new CustomEvent('vv:auth-expired'))
    }
  }
  if (res.status === 204) return undefined as T
  let json: unknown
  try {
    json = await res.json()
  } catch {
    json = null
  }
  if (!res.ok) {
    const err = (json as ErrorResponse | null)?.error
    const base = err?.message ?? `HTTP ${res.status}`
    const issues = (err?.details as { issues?: Array<{ message?: string; path?: string[] }> } | undefined)?.issues
    const detail = issues?.length
      ? issues.map((i) => i.message ?? '').filter(Boolean).join(' · ')
      : ''
    const message = detail ? `${base}: ${detail}` : base
    throw new ApiClientError(
      err?.code ?? 'UNKNOWN',
      message,
      res.status,
      err?.details,
    )
  }
  return json as T
}

/** multipart 上传(FormData,不设 content-type 让浏览器带 boundary) */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' }
  const token = tokenStore.get()
  if (token) headers.authorization = `Bearer ${token}`
  const res = await fetch(url(path), { method: 'POST', headers, body: form, credentials: 'include' })
  let json: unknown
  try {
    json = await res.json()
  } catch {
    json = null
  }
  if (!res.ok) {
    const err = (json as ErrorResponse | null)?.error
    throw new ApiClientError(err?.code ?? 'UNKNOWN', err?.message ?? `HTTP ${res.status}`, res.status, err?.details)
  }
  return json as T
}
